import React, { useMemo, useState } from 'react'
import { useDispatch } from 'dva'
import { Breadcrumb, message, Spin, Typography } from 'antd'
import { useLocation } from 'react-router-dom'
import {
  Title,
  DetailImg,
  InfoItem,
  Button,
  ProductPrice,
  DetailBreadcrumb,
} from '../components'
import { Spec, InputNumber, CategoryTitle } from 'components'
import { useRequest } from '@sisyphe/hooks'
import * as api from 'services/store'
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb'
import qs from 'query-string'
import styles from './index.module.less'

type Props = {}

type SearchProps = { spuCode?: string }

const Detail = (props: Props) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [breadcrumbItems, setBreadcrumbItems] = useState<ItemType[]>([])
  const [amount, setAmount] = useState(1)
  const [specValue, setSpecValue] = useState<
    SwaggerDTO.GoodsExtInfoDTO | undefined
  >()

  /**商城商品详情查询-App */
  const { data, loading } = useRequest(async () => {
    const search = location.search
    const { spuCode }: SearchProps = qs.parse(search)

    const { data: detailData } = await api.queryDetailQueryUsingPOST({
      spuCode: spuCode!,
    })
    const items = detailData?.frontCategoryTree?.map(c => ({
      title: (
        <CategoryTitle code={c.categoryCode}>{c?.categoryName}</CategoryTitle>
      ),
    }))

    // 查询库存
    const { data: stocks } = await api.judgeCacheUsingPOST({
      skuCodeList: detailData?.skus?.map(c => c.skuCode),
    })

    // 库存信息
    const skuStocks = {}
    stocks?.forEach(item => (skuStocks[item.skuCode] = item))

    setBreadcrumbItems([
      ...items,
      { title: <DetailBreadcrumb>{detailData.appShowName}</DetailBreadcrumb> },
    ])
    return { detailData, skuStocks }
  })

  const { detailData, skuStocks } = data || {}

  /**购物车管理-添加购物车 */
  const { run: shopCartAddUsingPOST, loading: addLoading } = useRequest(
    async () => {
      if (!specValue) {
        message.warning('请选择规格')
        return false
      }

      const { data: stocks } = await api.judgeCacheUsingPOST({
        skuCodeList: [specValue?.skuCode],
      })

      if (stocks?.[0]?.usableStock <= 0) {
        setAmount(0)
        message.warning('加购失败，商品已缺货')
        return false
      }

      // 提交时还需要校验库存
      if (amount > stocks?.[0]?.usableStock) {
        setAmount(stocks?.[0]?.usableStock)
        message.warning('加购数量超库存')
        return false
      }

      const params = {
        num: amount,
        price: specValue?.standardPrice,
        productType: detailData?.contentTtype,
        salePrice: specValue?.salePrice,
        skuCode: specValue?.skuCode,
        spuCode: detailData?.spuCode,
      }
      await api.shopCartAddUsingPOST(params as any)
      dispatch({ type: 'cart/getShopCartNum' })
      message.success('已加入购物车')
    },
    { manual: true }
  )

  const skuStock = useMemo(() => {
    if (!specValue?.skuCode) return 0
    return skuStocks?.[specValue?.skuCode || '']?.usableStock
  }, [specValue, skuStocks])

  // 规格选择
  const specChange = row => {
    setSpecValue(row)
    setAmount(1)
  }

  return (
    <Spin spinning={loading}>
      <div className={styles.box}>
        <div className={styles.booartDetail}>
          <Breadcrumb items={breadcrumbItems} />
          <div className={styles.detailInfo}>
            <DetailImg
              imgList={[
                detailData?.appImgUrl,
                ...(detailData?.bannerImage?.map(c => c?.mediaUrl) || []),
              ]}
            />
            <div className={styles.detail}>
              <Title className={styles.title}>{detailData?.appShowName}</Title>
              <InfoItem label="定 价：">
                <ProductPrice
                  minPrice={
                    specValue?.salePrice || detailData?.minStandardPrice
                  }
                  maxPrice={
                    specValue?.salePrice || detailData?.maxStandardPrice
                  }
                />
              </InfoItem>
              <InfoItem label="规 格：">
                <Spec
                  skuStocks={skuStocks}
                  value={specValue}
                  onChange={specChange}
                  dataSource={detailData?.skus}
                />
              </InfoItem>
              {!loading && (
                <AddCardButton
                  showTips={!!specValue?.skuCode}
                  detailData={detailData}
                  skuStock={skuStock}
                  amount={amount}
                  setAmount={setAmount}
                  addLoading={addLoading}
                  shopCartAddUsingPOST={shopCartAddUsingPOST}
                />
              )}
            </div>
          </div>
          <div className={styles.productDetail}>
            <p className={styles.productDetailTitle}>商品详情</p>
            <div className={styles.langImg}>
              {(specValue || detailData?.skus?.[0])?.longPic?.map(
                (item, index) => (
                  <img key={index} src={item} alt="" />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </Spin>
  )
}

export default Detail

const AddCardButton = ({
  detailData,
  skuStock,
  amount,
  setAmount,
  addLoading,
  shopCartAddUsingPOST,
  showTips = true,
}) => {
  if (detailData?.localCategoryDownFlag) {
    return <Typography.Title level={5}>抱歉，商品已下架</Typography.Title>
  }

  if (
    detailData?.spuStatus === 1 ||
    detailData?.spuAdvanceSale === 2 ||
    !detailData?.onSaleStatus ||
    detailData?.ifExpired
  ) {
    return <Typography.Title level={5}>抱歉，商品已下架</Typography.Title>
  }

  if (!detailData?.ifExpired && detailData?.spuStatus === 2) {
    return <Typography.Title level={5}>商品缺货</Typography.Title>
  }

  if (!detailData?.ifExpired && !detailData?.spuStatus) {
    return (
      <>
        <InfoItem label="数 量：">
          <InputNumber
            showTips={showTips}
            disabled={!skuStock}
            max={skuStock}
            value={amount}
            onChange={e => setAmount(e)}
          />
        </InfoItem>
        <Button
          className={styles.button}
          style={{ marginTop: '20px' }}
          type="primary"
          loading={addLoading}
          onClick={shopCartAddUsingPOST}
        >
          加入购物车
        </Button>
      </>
    )
  }

  return <></>
}
